import { ui } from "../lib/localization";
import { useCallback, useEffect, useMemo, useState } from "react";
import { LoaderCircle, MoreHorizontal, Pencil, Pin, Search, Trash2, X } from "lucide-react";
import type { Conversation, ModelInfo } from "../types";
import type { Language } from "../i18n";
import { getModel } from "../lib/models";
import { searchWorkspaceConversations } from "../lib/storage";
import { useModalDialog, isDialogBackdropClick } from "../lib/use-modal-dialog";
import { ConversationMenu } from "./ConversationMenu";

interface Props {
  conversations: Conversation[];
  activeId: string;
  models: ModelInfo[];
  language: Language;
  runningIds: string[];
  onSelect: (id: string) => void;
  onUpdate: (id: string, patch: Partial<Conversation>) => void;
  onDelete: (id: string) => void | Promise<void>;
  title: (value: string) => string;
}
const textMatches = (conversation: Conversation, query: string) => {
  const text = [conversation.title, conversation.draft?.content,
    ...conversation.messages.flatMap((message) => [message.content, message.reasoning, ...(message.attachments?.map((a) => `${a.name} ${a.text ?? ""}`) ?? [])]),
  ].join("\n").toLocaleLowerCase();
  return query.split(/\s+/).every((term) => text.includes(term));
};

export function ConversationHistory({ conversations, activeId, models, language, runningIds, onSelect, onUpdate, onDelete, title }: Props) {
  const [query, setQuery] = useState("");
  const [storedMatches, setStoredMatches] = useState<string[]>([]);
  const [searching, setSearching] = useState(false);
  const [renameId, setRenameId] = useState<string>();
  const [name, setName] = useState("");
  const [deleteId, setDeleteId] = useState<string>();
  const [deleting, setDeleting] = useState(false);
  const [deleteFailed, setDeleteFailed] = useState(false);
  const [menu, setMenu] = useState<{ id: string; anchor: HTMLElement }>();
  const closeMenu = useCallback(() => setMenu(undefined), []);
  const dialogRef = useModalDialog(Boolean(renameId || deleteId));
  const closeDialog = () => { if (!deleting) { setRenameId(undefined); setDeleteId(undefined); setDeleteFailed(false); } };
  const normalized = query.trim().toLocaleLowerCase();
  useEffect(() => {
    let alive = true;
    if (!normalized) { setStoredMatches([]); setSearching(false); return; }
    setSearching(true);
    const timer = setTimeout(() => {
      void searchWorkspaceConversations(normalized).then((ids) => { if (alive) setStoredMatches(ids); })
        .catch(() => { if (alive) setStoredMatches([]); }).finally(() => { if (alive) setSearching(false); });
    }, 180);
    return () => { alive = false; clearTimeout(timer); };
  }, [normalized]);
  const filtered = useMemo(() => conversations
    .filter((c) => !normalized || textMatches(c, normalized) || (c.messagesLoaded === false && storedMatches.includes(c.id)))
    .sort((a, b) => Number(Boolean(b.pinned)) - Number(Boolean(a.pinned)) || b.updatedAt.localeCompare(a.updatedAt)), [conversations, normalized, storedMatches]);
  const dateLabel = (conversation: Conversation) => {
    if (conversation.pinned) return ui(language, "Pinned");
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
    const time = Date.parse(conversation.updatedAt);
    return time >= +today ? ui(language, "Today") : time >= +yesterday ? ui(language, "Yesterday") : ui(language, "Earlier");
  };
  let previousGroup = "";
  const row = (c: Conversation) => <div className="conversation-row" key={c.id}>
    <button type="button" className={`conversation-item${c.id === activeId ? " active" : ""}`} aria-current={c.id === activeId ? "page" : undefined} onClick={() => onSelect(c.id)}>
      <span className="conversation-title">{c.pinned ? <Pin size={12} /> : null}{title(c.title)}</span>
      <span className="conversation-meta">{getModel(models, c.modelId).name}</span>
    </button>
    <button type="button" className="conversation-menu-trigger" aria-haspopup="menu" aria-expanded={menu?.id === c.id} aria-label={`${ui(language, "Conversation actions")}: ${title(c.title)}`}
      onClick={event => setMenu(menu?.id === c.id ? undefined : { id: c.id, anchor: event.currentTarget })}><MoreHorizontal size={17} /></button>
    {menu?.id === c.id ? <ConversationMenu anchor={menu.anchor} label={ui(language, "Conversation actions")} onClose={closeMenu}>
      <button role="menuitem" type="button" onClick={() => { setName(title(c.title)); setRenameId(c.id); }}><Pencil size={14} />{ui(language, "Rename")}</button>
      <button role="menuitem" type="button" onClick={() => onUpdate(c.id, { pinned: !c.pinned })}><Pin size={14} />{c.pinned ? ui(language, "Unpin") : ui(language, "Pin")}</button>
      <button role="menuitem" className="danger-action" type="button" onClick={() => setDeleteId(c.id)} disabled={runningIds.includes(c.id) || c.pendingGeneration || c.messages.some((m) => m.status === "streaming")}><Trash2 size={14} />{ui(language, "Delete conversation")}</button>
    </ConversationMenu> : null}
  </div>;
  return <>
    <label className="conversation-search"><Search size={15} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={ui(language, "Search conversations")} aria-label={ui(language, "Search conversations")} />{query ? <button type="button" onClick={() => setQuery("")} aria-label={ui(language, "Clear search")}><X size={14} /></button> : null}</label>
    <nav className="conversation-list" aria-label={ui(language, "Conversation history")}>
      {searching ? <p className="history-empty" role="status">{ui(language, "Searching…")}</p> : null}
      {!filtered.length && !searching ? <p className="history-empty">{normalized ? ui(language, "No matching conversations") : ui(language, "No conversations here")}</p> : null}
      {filtered.map((conversation) => {
        const label = dateLabel(conversation);
        const heading = previousGroup !== label ? label : null;
        previousGroup = label;
        return <div className="conversation-entry" key={conversation.id}>
          {heading ? <div className="history-date">{heading}</div> : null}
          {row(conversation)}
        </div>;
      })}
    </nav>
    <dialog ref={dialogRef} className="history-dialog" onCancel={(event) => { event.preventDefault(); closeDialog(); }} onClick={(event) => { if (isDialogBackdropClick(event)) closeDialog(); }} aria-label={renameId ? ui(language, "Rename conversation") : ui(language, "Delete conversation permanently")}>
      <form aria-busy={deleting} onSubmit={async (event) => {
        event.preventDefault();
        if (deleting) return;
        if (renameId && name.trim()) { onUpdate(renameId, { title: name.trim(), titleSource: "manual" }); closeDialog(); }
        else if (deleteId) {
          setDeleting(true); setDeleteFailed(false);
          try { await onDelete(deleteId); setDeleteId(undefined); }
          catch { setDeleteFailed(true); }
          finally { setDeleting(false); }
        }
      }}>
        <h2>{renameId ? ui(language, "Rename conversation") : ui(language, "Delete this conversation permanently?")}</h2>
        {renameId ? <input value={name} onChange={(event) => setName(event.target.value)} autoFocus maxLength={160} aria-label={ui(language, "Conversation name")} /> : <p>{ui(language, "This deletes the conversation from this browser and the server. This cannot be undone.")}</p>}
        {deleteFailed ? <p role="alert">{ui(language, "Could not delete the server conversation. Your local history is kept. Please retry.")}</p> : null}
        <div><button type="button" disabled={deleting} onClick={closeDialog}>{ui(language, "Cancel")}</button><button type="submit" disabled={deleting || Boolean(renameId && !name.trim())}>{deleting ? <LoaderCircle size={14} className="spinning" aria-hidden="true" /> : null}{renameId ? ui(language, "Save") : ui(language, "Delete permanently")}</button></div>
      </form>
    </dialog>
  </>;
}
